//@flow

import type {Action} from './actions';
import * as actions from './actions';
import type {PlayerData, PlayerContainer, ScheduledEpisode, Show, Season, State, VideoData, VideoList} from '../store';
import {isRoot, getBasicVideoTrackingLabel, getPlayingVideo, getDefaultVideo, nextVideoInContainer, watchableToVideoData, videoDataToPlayerData} from './../helpers';

import $ from 'jquery';
import ga from '../../../vendor/google-analytics';

export function changeSubnav(payload: string): Action {
  return {type: actions.CHANGE_SUBNAV, payload: payload};
}

export function changeSubnavMore(payload: string): Action {
  return {type: actions.CHANGE_SUBNAV_MORE, payload: payload};
}

export function clickDescriptionMore(): Action {
  return {type: actions.CLICK_DESCRIPTION_MORE, payload: null};
}

export function clickDescriptionClose(): Action {
  return {type: actions.CLICK_DESCRIPTION_CLOSE, payload: null};
}

export function setPosition(payload: number): Action {
  return {type: actions.SET_POSITION, payload: payload};
}

/**
 * ClipBar
 */

export function clickPreviewOn(history: ?Object): Function {
  return function(dispatch: Function, getState: () => State) {
    let state = getState();

    if (history) {
      let preview = state.player.container.preview;

      if (preview) {
        addToHistory(history, state.show, preview);
      }
    }

    dispatch({type: actions.CLICK_PREVIEW_ON, payload: null});
  };
}

export function clickPreviewOff(): Action {
  return {type: actions.CLICK_PREVIEW_OFF, payload: null};
}

export function endOfPreviewVideo(currentId: string, getState: () => State, history: ?Object): Function {
  return function(dispatch: Function) {
    let state = getState();

    // This action is only valid if the current video has not changed
    if (currentId === state.player.currentlyPlaying.id) {
      let next = state.player.container.fullLength;

      if (next) {
        if (history) {
          addToHistory(history, state.show, next);
        }

        return dispatch({type: actions.LOAD_VIDEO, payload: videoDataToPlayerData(next, false)});
      }
    }
  }
}

export function clickClipsOn(): Action {
  return {type: actions.CLICK_CLIPS_ON, payload: null};
}

export function clickClipsOff(): Action {
  return {type: actions.CLICK_CLIPS_OFF, payload: null};
}

export function clickClipLArrow(): Action {
  return {type: actions.CLICK_CLIP_L_ARROW, payload: null};
}

export function clickClipRArrow(): Action {
  return {type: actions.CLICK_CLIP_R_ARROW, payload: null};
}

export function changeClipPage(page: number): Action {
  return {type: actions.CLIP_CHANGE_PAGE, payload: page};
}

export function changeClipPageSize(size: number): Action {
  return {type: actions.CLIP_CHANGE_PAGE_SIZE, payload: size};
}

export function endOfClipVideo(currentId: string, getState: () => State, history: ?Object) {
  return function(dispatch: Function) {
    let state = getState();

    // This action is only valid if the current video has not changed
    if (currentId === state.player.currentlyPlaying.id) {
      let next = nextVideoInContainer(state.player.container, currentId);
      let fullLength = state.player.container.fullLength && state.player.container.fullLength !== null && state.player.container.fullLength || null;

      if (next) {
        if (history) {
          addToHistory(history, state.show, next);
        }

        // Only autoplay if the video to be played is the full episode
        let autoplay = !fullLength || fullLength.id !== next.id;

        return dispatch({type: actions.LOAD_VIDEO, payload: videoDataToPlayerData(next, autoplay)});
      }
    }
  }
}

function addToHistory(history, show: Show, video: VideoData) {
  history.push(`/${show.slug}/video/${video.slug}/`);
}

function fetchVideo(postId: number, id: string): Promise<Object> {
  return $.ajax({
    method: 'GET',
    url: window.location.origin.replace(/http:/, 'https:') + '/api/v1/show/' + postId + '/watchables/' + id + '/extended/',
    contentType: 'application/json',
    dataType: 'json'
  });
}

function loadWatchable(postId: number, key: string, dispatch: Function): Promise<PlayerContainer> {
  return fetchVideo(postId, key)
    .then(response => {
      return watchableToVideoData(response);
    })
    .then(container => {
      dispatch(receivedVideoData(container));
      return container;
    });
}

function loadVideo(postId: number, attr: 'slug'|'id', value: string, dispatch: Function, getState: () => State): Promise<VideoData> {
  let video = getPlayingVideo(getState().player.container, attr, value);

  if (video) {
    return Promise.resolve(video);
  } else {
    return loadWatchable(postId, value, dispatch)
      .then(container => {
        let video = getPlayingVideo(container, attr, value);

        if (video) {
          return video;
        } else {
          throw 'Failed to find video in loaded container';
        }
      });
  }
}

export function loadPlayerData(postId: number, data: PlayerData, getState: () => State, history: ?Object): Function {
  return function(dispatch) {
    loadVideo(postId, 'id', data.id, dispatch, getState)
      .then(video => {
        if (history) {
          addToHistory(history, getState().show, video);
        }

        return videoDataToPlayerData(video, data.autoplay);
      })
      .then(playerData => {
        dispatch({type: actions.LOAD_VIDEO, payload: playerData});
      });
  };
}

export function loadVideoById(postId: number, id: string, getState: () => State): Function {
  return function(dispatch) {
    loadVideo(postId, 'id', id, dispatch, getState)
      .then(video => {
        return videoDataToPlayerData(video, true);
      })
      .then(playerData => {
        dispatch({type: actions.LOAD_VIDEO, payload: playerData});
      });
  };
}

export function loadVideoBySlug(postId: number, slug: string, getState: () => State): Function {
  return function(dispatch) {
    loadVideo(postId, 'slug', slug, dispatch, getState)
      .then(video => {
        return videoDataToPlayerData(video, true);
      })
      .then(playerData => {
        dispatch({type: actions.LOAD_VIDEO, payload: playerData});
      });
  };
}

export function fetchShowData(postId: number): Function {
  return function(dispatch: Function) {
    dispatch(fetchingShowData());

    $.ajax({
      method: 'GET',
      url: window.location.origin.replace(/http:/, 'https:') + '/api/v1/show/' + postId + '/',
      contentType: 'application/json',
      dataType: 'json',
      error: function (xhr, ajaxOptions, throwError) {
        dispatch(fetchingShowDataError(throwError));
      },
    })
      .then(function (response) {
        let livestreamService = null;

        if (response.livestream) {
          livestreamService = {
            name: response.livestream.service.name,
            account: response.livestream.service.account,
            embed: response.livestream.service.embed,
            start: response.livestream.service.start,
            end: response.livestream.service.end,
            title: response.livestream.service.title,
            description: response.livestream.service.description
          };
        }

        let storyboard = null;

        if (response.related_storyboard_post) {
          storyboard = {
            title: response.related_storyboard_post.title,
            url: response.related_storyboard_post.url
          }
        }

        let responseData = {
          fetchingShow: false,
          fetchedShow: true,
          error: null,
          id: response.post.ID,
          title: response.post.post_title,
          slug: response.post.post_name,
          airings: response.upcoming_airdates,
          episodeAirings: [],
          description: response.post.post_content,
          mezzanine: response.mezzanine,
          website_url: response.website_url,
          site_button_text: response.site_button_text,
          related_shows: response.related_shows,
          resourceLinks: {
            DVD: [],
            externalSite: [],
          },
          livestream: {
            isLive: response.livestream && response.livestream.is_live,
            service: livestreamService
          },
          storyboard: storyboard,
          blocks: response.blocks,
          promos: response.promos,
          seasons: response.seasons.sort((s1, s2) => s2.ordinal - s1.ordinal),
          sponsors: response.sponsors
        };
        dispatch(receivedShowData(responseData));

        return responseData;
      });
  }
}

export function fetchingShowData(): Action {
  return {type: actions.FETCHING_SHOW_DATA, payload: null};
}

export function fetchingShowDataError(payload: Error): Action {
  return {type: actions.FETCHING_SHOW_DATA_ERROR, payload: payload};
}

export function receivedShowData(payload: Show): Action {
  return {type: actions.RECEIVED_SHOW_DATA, payload: payload}
}

export function loadLivestream(getState: () => State): Action {
  let show = getState().show;
  return {type: actions.LOAD_LIVESTREAM, payload: show.livestream && show.livestream.isLive};
}

export function changeSeason(postId: number, season: Season) { // ajax call to get video data and update video list (full episodes). update store.
  return function(dispatch: Function) {
    dispatch({type: actions.CHANGE_SEASON, payload: season});
    fetchVideoList(postId, season)(dispatch);
  };
}

export function clickChangeSpecial(payload: string) { // same as season dropdown for now
  return {type: actions.CLICK_CHANGE_SPECIAL, payload: payload};
}

export function clickShowAllAirdates(postId: number, getState: () => State) {
  return function(display: boolean): Function {
    return function(dispatch: Function) {
      let state = getState();

      dispatch({type: actions.CLICK_SHOW_ALL_AIRDATES, payload: display});

      if (state.show.airings.length > 1 && state.show.episodeAirings.length === 0) {
        $.ajax({
          method: 'GET',
          url: window.location.origin.replace(/http:/, 'https:') + '/api/v1/show/' + postId + '/airings/',
          contentType: 'application/json',
          dataType: 'json'
        }).then(function(episodes) {
          dispatch(receivedScheduledEpisodes(
            episodes.map(function(episode: *): ScheduledEpisode {
              return {
                episodeNumber: episode.episode_number,
                title: episode.title,
                description: episode.desc,
                airings: episode.airings
              }
            })
          ));
        });
      }
    };
  }
}

export function receivedScheduledEpisodes(episodes: Array<ScheduledEpisode>): Action {
  return {type: actions.RECEIVED_SCHEDULED_EPISODES, payload: episodes};
}

export function clickFacebookShare() {
  return {type: actions.SHARE_FACEBOOK, payload: null};
}

export function clickTwitterShare() {
  return {type: actions.SHARE_TWITTER, payload: null};
}

export function clickEmailShare() {
  return {type: actions.SHARE_EMAIL, payload: null};
}

export function clickEmbedShare() {
  return {type: actions.SHARE_EMBED, payload: null};
}

export function autoplayCancel() {
  return {type: actions.AUTOPLAY_CANCEL, payload: null};
}

export function autoplayTimerTick(getState: () => State,  history: ?Object) {
  return (dispatch: Function) => {
    if (getState().player.endScreen.autoplayTimer.timeLeft >= 1) {
      dispatch({type: actions.AUTOPLAY_TIMER_TICK, payload: null });
    } else {
      dispatch(autoplayTimerExpire(getState, history));
    }
  }
}

export function autoplayTimerExpire(getState: () => State,  history: ?Object): Function {
  return (dispatch: Function) => {
    let video = getState().player.endScreen.nextVideo;

    if (video) {
      let full = video.fullLength;

      if (full) {

        // Get the base tracking label
        let baseLabel = getBasicVideoTrackingLabel(getState().show, getState().player);

        if (baseLabel) {

          // Add in the next video data
          let label = [baseLabel, full.title, full.tpMediaId].join(' | ');

          // TODO: Move this analytics call to a more appropriate location when tracking is refactored
          ga('send', 'event', 'VideoPlayer | Endscreen', 'Autoplay Triggered | [P]', label);
        }

        dispatch(receivedVideoData(video));

        if (history) {
          addToHistory(history, getState().show, full);
        }

        dispatch({type: actions.LOAD_VIDEO, payload: videoDataToPlayerData(full, true)});
      }
    }
  }
}

export function returnToVideo(): Action {
  return {type: actions.CLOSE_END_SCREEN, payload: null};
}

export function fetchFeaturedVideo(postId: number): Function {
  return (dispatch: Function) => {
    dispatch(fetchingVideoData());

    $.ajax({
      method: 'GET',
      url: window.location.origin.replace(/http:/, 'https:') + '/api/v1/show/' + postId + '/watchables/featured/',
      contentType: 'application/json',
      dataType: 'json',
      error: function (xhr, ajaxOptions, throwError) {
        dispatch(fetchingVideoDataError(throwError));
      },
    }).then(function(response) {
      let container = watchableToVideoData(response);
      dispatch(receivedVideoData(container));

      return container;
    }).then(function(container) {
      let video = getDefaultVideo(container);

      if (video) {
        dispatch({type: actions.LOAD_VIDEO, payload: videoDataToPlayerData(video)});
      }
    });
  }
}

export function fetchingVideoData(): Action {
  return {type: actions.FETCHING_VIDEO_DATA, payload: null};
}

export function fetchingVideoDataError(payload: Error): Action {
  return {type: actions.FETCHING_VIDEO_DATA_ERROR, payload: payload};
}

export function receivedVideoData(payload: PlayerContainer): Action {
  return {type: actions.RECEIVED_VIDEO_DATA, payload: payload}
}

export function fetchVideoList(postId: number, season: Season): Function {
  return (dispatch: Function) => {
    $.ajax({
      method: 'GET',
      url: window.location.origin.replace(/http:/, 'https:') + '/api/v1/show/' + postId + '/watchables/?season=' + season.id,
      contentType: 'application/json',
      dataType: 'json',
      error: function (xhr, ajaxOptions, throwError) {
        dispatch(fetchingVideoDataError(throwError));
      },
    }).done(function (response) {
      dispatch(
        receivedVideoList(
          {
            season: season,
            fullLengthVideos: response.map(watchableToVideoData).filter(v => v.fullLength !== null),
            fetchingVideoList: false,
            fetchedVideoList: true,
            error: null
          }
        )
      );
    });
  }
}

export function fetchingVideoList(): Action {
  return {type: actions.FETCHING_VIDEO_LIST, payload: null};
}

export function fetchingVideoListError(payload: Error): Action {
  return {type: actions.FETCHING_VIDEO_LIST_ERROR, payload: payload};
}

export function receivedVideoList(payload: VideoList): Action {
  return {type: actions.RECEIVED_VIDEO_LIST, payload: payload}
}

export function clickOnVideo(state: State): Action {
  return {type: actions.CLICK_ON_VIDEO, payload: null};
}

export function playerPlay(): Action {
  return {type: actions.PLAYER_PLAY, payload: null};
}

export function playerPause(): Action {
  return {type: actions.PLAYER_PAUSE, payload: null};
}

export function playerStop(): Action {
  return {type: actions.PLAYER_STOP, payload: null};
}

export function playerSeek(): Action {
  return {type: actions.PLAYER_SEEK, payload: null};
}

export function videoEnd(postId: number, key: string): Function {
  return function(dispatch) {
    $.ajax({
      method: 'GET',
      url: window.location.origin.replace(/http:/, 'https:') + '/api/v1/show/' + postId + '/watchables/' + key + '/next/extended/',
      contentType: 'application/json',
      dataType: 'json'
    })
      .then(function(response) {
        dispatch({type: actions.VIDEO_END, payload: watchableToVideoData(response)});
    });
  }
}